/**
 * 掲示板スレッドパネル（右スライドイン）。
 * 仕様書: docs/02_work/done/plan_board_feature.md §1-5, §2-3
 *
 * - テキスト + 画像メッセージ（画像は uploadBoardImage の 5MB パイプライン）。
 * - 本文の URL は LinkifiedText で自動リンク化。
 * - 絵文字リアクション: 各メッセージ右下に最新2グループ + '…'。
 *   グループ / '…' クリック → リアクション一覧ポップ（新しい順、自分の分は取消可）。
 *   '+' → BOARD_REACTION_EMOJIS パレット（トグル、自分の分はハイライト）。
 * - 自分のメッセージは 編集 / 削除。
 * - パネルの開閉時に markThreadSeen を呼ぶ（一覧・詳細の新着バッジを消す）。
 * - 家訓: onSnapshot コールバック内は setState のみ（購読は useThreadMessages が担当）。
 */
import { BOARD_REACTION_EMOJIS } from '@goenhub/shared';
import { ImagePlus, Loader2, Send, Smile, X } from 'lucide-react';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type RefObject,
} from 'react';

import { LinkifiedText } from '@/components/common/LinkifiedText';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from '@/components/ui/toast';
import { useAdminDirectory } from '@/hooks/useAdminDirectory';
import { useAuth } from '@/hooks/useAuth';
import {
  useThreadActions,
  useThreadMessages,
  type ThreadMessageRecord,
} from '@/hooks/useBoardThread';
import { useVisualViewport } from '@/hooks/useVisualViewport';
import { uploadBoardImage } from '@/lib/boardMedia';
import { formatDateTime } from '@/lib/firestoreTime';

/**
 * textarea を内容量に合わせて自動伸縮させる。
 * 2 行以上でも全文が見える（スクロールしないと読めない状態を作らない）。
 * 上限（CSS の max-height）に達したときだけ内部スクロールに切り替わる。
 */
function useAutosizeTextarea(ref: RefObject<HTMLTextAreaElement>, value: string) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [ref, value]);
}

/**
 * 添付画像 URL の安全判定。imageUrls は firestore.rules で検証されない
 * クライアント直書きフィールドのため、https の Storage URL のみを描画対象にし、
 * javascript:/data: 等を <img src> / <a href> に流し込む格納 XSS を防ぐ。
 */
function isSafeImageUrl(url: string): boolean {
  return /^https:\/\//i.test(url);
}

export function ThreadPanel({
  postId,
  open,
  onOpenChange,
}: {
  postId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const directory = useAdminDirectory();
  const { messages } = useThreadMessages(postId, open);
  const actions = useThreadActions();
  // markThreadSeen は useCallback で安定。effect 依存にはこの関数参照だけを使う
  // （actions オブジェクトは毎レンダー新参照のため deps に入れると無限再実行になる）。
  const { markThreadSeen } = actions;

  /** uid → 表示名（ディレクトリ優先、フォールバックはメッセージのスナップショット名）。 */
  const nameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of directory.data ?? []) {
      map.set(m.uid, m.fullName || m.displayName || m.uid);
    }
    return map;
  }, [directory.data]);

  const nameOf = (u: string, fallback: string) => nameMap.get(u) ?? fallback ?? u;

  // 既読化: 開いたとき + 閉じたとき（cleanup）に markThreadSeen を呼ぶ。
  useEffect(() => {
    if (!open || !postId) return;
    void markThreadSeen(postId);
    return () => {
      void markThreadSeen(postId);
    };
  }, [open, postId, markThreadSeen]);

  // 新着メッセージへ自動スクロール。
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, open]);

  // モバイル: ソフトキーボードで縮んだ「見えている領域」にパネルを合わせる。
  // これをしないと下端の入力欄が画面外に出て操作不能になる。
  const viewport = useVisualViewport(open);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        aria-describedby={undefined}
        style={
          viewport
            ? { height: `${viewport.height}px`, top: `${viewport.offsetTop}px` }
            : undefined
        }
      >
        <SheetHeader>
          <SheetTitle>🧵 スレッド（{messages.length}）</SheetTitle>
        </SheetHeader>

        {/* メッセージ一覧 (min-h-0: flex 子の overflow を効かせる) */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">
          {messages.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] text-center py-8">
              まだメッセージはありません。最初のメッセージを送信しましょう。
            </p>
          ) : (
            messages.map((m) => (
              <MessageItem
                key={m.id}
                postId={postId}
                message={m}
                uid={uid}
                displayName={nameOf(m.authorUid, m.authorName)}
                actions={actions}
                nameOf={nameOf}
              />
            ))
          )}
          <div ref={bottomRef} />
        </div>

        {/* 入力欄 */}
        <Composer postId={postId} onSend={actions.sendThreadMessage} />
      </SheetContent>
    </Sheet>
  );
}

// ─────────────────────────────────────────────
// メッセージ 1 件
// ─────────────────────────────────────────────

type ThreadActions = ReturnType<typeof useThreadActions>;

function MessageItem({
  postId,
  message,
  uid,
  displayName,
  actions,
  nameOf,
}: {
  postId: string;
  message: ThreadMessageRecord;
  uid: string | null;
  displayName: string;
  actions: ThreadActions;
  nameOf: (u: string, fallback: string) => string;
}) {
  const isMine = Boolean(uid && message.authorUid === uid);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(message.body);
  const [popup, setPopup] = useState<'none' | 'who' | 'palette'>('none');
  const popupRef = useRef<HTMLDivElement>(null);
  const editRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextarea(editRef, editing ? editValue : '');

  // ポップの外側クリックで閉じる。
  useEffect(() => {
    if (popup === 'none') return;
    const onDocClick = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setPopup('none');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [popup]);

  /** 絵文字ごとにグルーピング（最新の出現位置 = 表示順の新しさ）。 */
  const groups = useMemo(() => {
    const map = new Map<string, { emoji: string; count: number; lastIndex: number }>();
    message.reactions.forEach((r, i) => {
      const g = map.get(r.emoji) ?? { emoji: r.emoji, count: 0, lastIndex: i };
      g.count += 1;
      g.lastIndex = i;
      map.set(r.emoji, g);
    });
    return [...map.values()].sort((a, b) => b.lastIndex - a.lastIndex);
  }, [message.reactions]);

  const latestGroups = groups.slice(0, 2);
  const hasMore = groups.length > 2;

  /** 自分がその絵文字を付けているか。 */
  const mineHas = (emoji: string) =>
    Boolean(uid && message.reactions.some((r) => r.uid === uid && r.emoji === emoji));

  const handleToggle = async (emoji: string) => {
    try {
      await actions.toggleReaction(postId, message.id, emoji, mineHas(emoji));
    } catch (err) {
      toast.error(`リアクション失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleSaveEdit = async () => {
    const body = editValue.trim();
    if (!body) {
      toast.error('本文を入力してください');
      return;
    }
    try {
      await actions.editThreadMessage(postId, message.id, body);
      setEditing(false);
    } catch (err) {
      toast.error(`編集失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('このメッセージを削除します。よろしいですか？')) return;
    try {
      await actions.deleteThreadMessage(postId, message.id);
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // リアクション一覧（新しい順 = 配列の末尾から）。
  const reactorsNewestFirst = useMemo(
    () => [...message.reactions].reverse(),
    [message.reactions],
  );

  return (
    <div className="flex gap-2.5">
      {/* アバター */}
      <div className="size-8 rounded-full bg-[var(--bg-hover)] flex items-center justify-center text-xs text-[var(--text-secondary)] flex-shrink-0">
        {displayName.charAt(0)}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-[var(--text-primary)]">
            {displayName}
          </span>
          <span className="text-[11px] text-[var(--text-muted)]">
            {formatDateTime(message.createdAt)}
          </span>
          {message.editedAt && (
            <span className="text-[11px] text-[var(--text-muted)]">（編集済）</span>
          )}
        </div>

        {/* 本文 */}
        {editing ? (
          <div className="mt-1 space-y-1.5">
            <textarea
              ref={editRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              rows={1}
              className="w-full max-h-[50vh] overflow-y-auto rounded-sm border border-[var(--border)] bg-[var(--bg-surface)] px-2 py-1.5 text-sm text-[var(--text-primary)] resize-none focus:outline-none focus:border-[var(--accent)]"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void handleSaveEdit()}
                className="px-2.5 py-1 rounded-sm text-xs border border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]"
              >
                保存
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setEditValue(message.body);
                }}
                className="px-2.5 py-1 rounded-sm text-xs border border-[var(--border)] text-[var(--text-secondary)]"
              >
                キャンセル
              </button>
            </div>
          </div>
        ) : (
          message.body && (
            <LinkifiedText
              text={message.body}
              className="block mt-0.5 text-sm text-[var(--text-primary)]"
            />
          )
        )}

        {/* 画像サムネイル。imageUrls は rules で検証されないクライアント直書きのため、
            https の Storage URL のみ描画する (javascript:/data: 等での格納 XSS を防ぐ)。 */}
        {message.imageUrls.filter(isSafeImageUrl).length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-2">
            {message.imageUrls.filter(isSafeImageUrl).map((url) => (
              <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                <img
                  src={url}
                  alt="添付画像"
                  className="max-h-40 rounded-sm border border-[var(--border)] object-cover hover:opacity-90"
                />
              </a>
            ))}
          </div>
        )}

        {/* リアクション行 + アクション */}
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap relative">
          {/* 最新2グループ。クリックでリアクション一覧ポップを開く（トグルはパレット / 一覧内から）。 */}
          {latestGroups.map((g) => {
            const mine = mineHas(g.emoji);
            return (
              <button
                key={g.emoji}
                type="button"
                onClick={() => setPopup((p) => (p === 'who' ? 'none' : 'who'))}
                className={
                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border transition-colors ' +
                  (mine
                    ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                    : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]')
                }
                title="リアクションした人を表示"
              >
                <span>{g.emoji}</span>
                <span className="tabular-nums">{g.count}</span>
              </button>
            );
          })}

          {/* '…' = 3グループ目以降がある場合の一覧アクセス */}
          {hasMore && (
            <button
              type="button"
              onClick={() => setPopup((p) => (p === 'who' ? 'none' : 'who'))}
              className="px-1.5 py-0.5 rounded-full text-xs border border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]"
              title="すべてのリアクション"
            >
              …
            </button>
          )}

          {/* '+' パレット */}
          <button
            type="button"
            onClick={() => setPopup((p) => (p === 'palette' ? 'none' : 'palette'))}
            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs border border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--text-muted)] hover:text-[var(--text-secondary)]"
            title="リアクションを追加"
          >
            <Smile className="size-3.5" />
          </button>

          {isMine && !editing && (
            <>
              <button
                type="button"
                onClick={() => {
                  setEditValue(message.body);
                  setEditing(true);
                }}
                className="ml-1 text-[11px] text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
              >
                編集
              </button>
              <button
                type="button"
                onClick={() => void handleDelete()}
                className="text-[11px] text-[var(--text-muted)] hover:text-[var(--accent-red,var(--text-secondary))]"
              >
                削除
              </button>
            </>
          )}

          {/* ポップ（一覧 / パレット） */}
          {popup !== 'none' && (
            <div
              ref={popupRef}
              className="absolute top-full left-0 mt-1 z-10 min-w-[180px] max-w-[260px] rounded-md border border-[var(--border)] bg-[var(--bg-surface)] shadow-lg p-2"
            >
              {popup === 'palette' ? (
                <div className="flex flex-wrap gap-1">
                  {BOARD_REACTION_EMOJIS.map((emoji) => {
                    const mine = mineHas(emoji);
                    return (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => {
                          void handleToggle(emoji);
                          setPopup('none');
                        }}
                        className={
                          'size-8 rounded-sm text-base flex items-center justify-center border transition-colors ' +
                          (mine
                            ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                            : 'border-transparent hover:bg-[var(--bg-hover)]')
                        }
                      >
                        {emoji}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-1 max-h-52 overflow-y-auto">
                  {reactorsNewestFirst.length === 0 ? (
                    <p className="text-[11px] text-[var(--text-muted)]">
                      リアクションはありません
                    </p>
                  ) : (
                    reactorsNewestFirst.map((r, i) => {
                      const own = Boolean(uid && r.uid === uid);
                      return (
                        <div
                          key={`${r.uid}-${r.emoji}-${i}`}
                          className="flex items-center justify-between gap-2 text-xs"
                        >
                          <span className="flex items-center gap-1.5 min-w-0">
                            <span>{r.emoji}</span>
                            <span className="truncate text-[var(--text-secondary)]">
                              {nameOf(r.uid, r.uid)}
                            </span>
                          </span>
                          {own && (
                            <button
                              type="button"
                              onClick={() => void handleToggle(r.emoji)}
                              className="text-[11px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] flex-shrink-0"
                            >
                              取消
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// 入力欄
// ─────────────────────────────────────────────

function Composer({
  postId,
  onSend,
}: {
  postId: string;
  onSend: (postId: string, input: { body: string; imageUrls: string[] }) => Promise<void>;
}) {
  const [body, setBody] = useState('');
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  // 入力に合わせて伸縮（送信で空になれば 1 行に戻る）。
  useAutosizeTextarea(bodyRef, body);

  const handlePickImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 同じファイルの再選択を許可
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadBoardImage(postId, file);
      setImageUrls((prev) => [...prev, url]);
    } catch (err) {
      toast.error(`画像アップロード失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setUploading(false);
    }
  };

  const canSend = (body.trim().length > 0 || imageUrls.length > 0) && !uploading && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await onSend(postId, { body: body.trim(), imageUrls });
      setBody('');
      setImageUrls([]);
    } catch (err) {
      toast.error(`送信失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="border-t border-[var(--border)] p-3 flex-shrink-0 space-y-2"
      // ホームインジケータ（iPhone）に送信ボタンが被らないようにする。
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      {/* 添付済み画像プレビュー */}
      {imageUrls.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {imageUrls.map((url) => (
            <div key={url} className="relative">
              <img
                src={url}
                alt="添付候補"
                className="size-16 rounded-sm border border-[var(--border)] object-cover"
              />
              <button
                type="button"
                onClick={() => setImageUrls((prev) => prev.filter((u) => u !== url))}
                className="absolute -right-1.5 -top-1.5 size-4 rounded-full bg-[var(--bg-surface)] border border-[var(--border)] flex items-center justify-center text-[var(--text-secondary)]"
                title="削除"
              >
                <X className="size-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => void handlePickImage(e)}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="size-9 flex-shrink-0 rounded-sm border border-[var(--border)] flex items-center justify-center text-[var(--text-secondary)] hover:border-[var(--text-muted)] disabled:opacity-50"
          title="画像を添付"
        >
          {uploading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <ImagePlus className="size-4" />
          )}
        </button>
        <textarea
          ref={bodyRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter で送信
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              void handleSend();
            }
          }}
          rows={1}
          placeholder="メッセージを入力（Cmd/Ctrl+Enter で送信）"
          // max-h-[40vh]: ここまでは全文表示、超えた分だけ内部スクロール。
          className="flex-1 min-w-0 min-h-[2.25rem] max-h-[40vh] overflow-y-auto rounded-sm border border-[var(--border)] bg-[var(--bg-surface)] px-2.5 py-2 text-sm text-[var(--text-primary)] resize-none focus:outline-none focus:border-[var(--accent)]"
        />
        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={!canSend}
          className="size-9 flex-shrink-0 rounded-sm border border-[var(--accent)] bg-[var(--accent)] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
          title="送信"
        >
          {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        </button>
      </div>
    </div>
  );
}
